import { useContext, useEffect, useMemo } from 'react';
import './App.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import "react-image-gallery/styles/scss/image-gallery.scss";
import { generateRandomString } from './utils/stringHelper';
import { useLoader } from './customHooks/loader';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ROUTES, STOREFRONT_URLS } from './routes/Routes';
import { LAUNCH_MODE } from './siteConfig';
import { NewsletterModal } from './components/Newsletter/NewsletterModal';
import { Header } from './components/Header/Header';
import { PrivateRoutes } from './routes/PrivateRoutes';
import Footer from './components/Footer/Footer';
import { AuthRoutes } from './routes/AuthRoutes';
import { AuthContext } from './context';
import { REMOVE_USER, SET_USER, TOKEN } from './constants';
import { UserService } from './services/UserService';
import { CartService } from './services/CartService';
import { globalErrorHandler } from './utils/errorHandler';
import { useCart } from './customHooks/cart';

export const App = () => {
  const privateRoutes = useMemo(() =>
    ROUTES.flatMap((route) =>
      route.isPrivate
        ? [
          ...(route.childRoutes || []).map((childRoute) => (
            <Route
              key={`${route.url + childRoute.url}`}
              path={`${route.url + childRoute.url}`}
              element={childRoute.component}
            />
          )),
          <Route key={route.url} path={route.url} element={route.component} />,
        ]
        : []
    ).filter(Boolean),
    [ROUTES]);

  const publicRoutes = useMemo(() =>
    ROUTES.filter((route) => !route.isPrivate).map((publicRoute) => (
      <Route key={publicRoute.url} path={publicRoute.url} element={publicRoute.component} />
    )),
    [ROUTES]);

  // Only the auth screens belong behind AuthRoutes — it bounces signed-in users
  // to `/`, so wrapping `/` itself in it would redirect the home page to itself.
  const authScreenRoutes = useMemo(() =>
    ROUTES.filter((route) => route.isAuthScreen).map((route) => (
      <Route key={route.url} path={route.url} element={route.component} />
    )),
    [ROUTES]);

  const openRoutes = useMemo(() =>
    ROUTES.filter((route) => !route.isPrivate && !route.isAuthScreen).map((route) => (
      <Route key={route.url} path={route.url} element={route.component} />
    )),
    [ROUTES]);
  const loader = useLoader();
  const cart = useCart();
  const { start: startLoader, stop: stopLoader } = loader(generateRandomString(5));
  const { refreshCart } = cart();

  const { dispatch: authDispatch } = useContext(AuthContext);

  const dispatchSetUser = (payload) => {
    authDispatch({ type: SET_USER, payload });
  };

  const removeUser = () => authDispatch({ type: REMOVE_USER });

  const handleVerifyError = () => {
    removeUser();
  };

  const handleVerifySuccess = ({ data }) => {
    dispatchSetUser({ user: data.data.user });
    refreshCart(data.data.cartItems)
  };

  useEffect(() => {
    // No signed-in experience during launch mode, so skip the token round-trip.
    // A stale token from a previous visit would otherwise fire a verify call and
    // surface an error toast on the landing page.
    if (LAUNCH_MODE) return;

    if (localStorage.getItem(TOKEN)) {
      UserService.verify(handleVerifySuccess, handleVerifyError, loader);
    }
  }, []);

  useEffect(() => {
    startLoader();
    const timer = setTimeout(() => stopLoader(), 1000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className='App'>
      <Header />
      <Routes>
        {LAUNCH_MODE ? (
          // Launch mode: no auth, no guards. Only `/` and the footer info pages
          // exist; every storefront URL is caught below and sent back to `/`.
          publicRoutes
        ) : (
          <>
            <Route element={<PrivateRoutes />}>
              {privateRoutes}
            </Route>
            <Route element={<AuthRoutes />}>
              {authScreenRoutes}
            </Route>
            {openRoutes}
          </>
        )}

        {LAUNCH_MODE
          ? STOREFRONT_URLS.map((url) => (
            <Route key={url} path={url} element={<Navigate to="/" replace />} />
          ))
          : null}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {LAUNCH_MODE ? <NewsletterModal /> : null}
      <Footer />
    </div>
  );
};
